import { ShopFilter } from "@b2bportal/air-shopping-api";
import {
  PackageType,
  type ChosenFlight,
  type LodgingAvailabilityRequest,
  ProductTypeFunnels,
  type Requisition,
  type FlightShopRequest,
  type CarAvailabilityRequest,
} from "@b2bportal/core-packages";
import { Funnel } from "@b2bportal/core-types";
import { CodeContextEnum } from "@b2bportal/ground-api";

import { Platform } from "@b2bportal/lodging-api";
import { PackageAvailabilityRequestEnum } from "@b2bportal/packages-api";
import { ensureExhaustive, getDeviceType, logger } from "@overrides/utilities";
import { createAsyncThunk } from "@reduxjs/toolkit";
import type { PackageSearchQueryParams } from "@tenant/types";
import { PackagesBaseActions } from "../slice/packages";
import type {
  PackageSearchState,
  PackagesSearch,
} from "../types/packagesSearch";
import { buildPackageFlightShopPassengers } from "./flightPassengerUtils";
import { fetchPackageFlightsRequisition } from "./flights/fetchPackageFlightsRequisition";
import { fetchPackageSearchParams } from "./general";
import { fetchPackageHotelAvailability } from "./hotels/fetchPackageHotelAvailability";
import { abortCurrentHotelAvailabilityMore } from "./hotels/fetchPackageHotelAvailabilityMore";
import { buildLodgingChildrenAges } from "./lodgingGuestUtils";
import {
  formatPackageDynamicFieldDates,
  useFormatPackagePickupDropoffDates,
} from "@/pages/Packages/hooks";

export const initializePackages = createAsyncThunk<
  PackageSearchState,
  { search: PackageSearchQueryParams; chosenFlight?: ChosenFlight }
>("packages/initialize", async ({ search, chosenFlight }, thunkAPI) => {
  // Cancel any in-flight follow-up lodging availability (fetchMore) chain
  // from a previous requisition before starting a new one.
  abortCurrentHotelAvailabilityMore();

  const { isDesktopAndUp } = getDeviceType();
  try {
    const requestedSearch = await thunkAPI
      .dispatch(fetchPackageSearchParams(search))
      .unwrap();

    // If a newer search has already aborted this thunk, stop before
    // dispatching any sub-thunks / customize calls.
    if (thunkAPI.signal.aborted) {
      return thunkAPI.rejectWithValue("Aborted by newer search");
    }

    const origin = requestedSearch.origin;
    // In an upsell the packages autocomplete label may differ from what the
    // user searched in flights. Override it with the original flight
    // destination label so the destination field shows a familiar label,
    // while still using the packages autocomplete data for the actual search.
    const destination = chosenFlight
      ? {
          ...requestedSearch.destination,
          label: decodeURIComponent(search.destination),
        }
      : requestedSearch.destination;
    const resolvedSearch = chosenFlight
      ? { ...requestedSearch, destination }
      : requestedSearch;
    const funnelsToSearch = ProductTypeFunnels[requestedSearch.packageType];

    // Check if this package includes flights to determine if we need dynamic date adjustments
    const includesFlights = funnelsToSearch.includes(Funnel.Air);

    // construct requesition
    const products: Requisition["products"] = [];

    for (const funnel of funnelsToSearch) {
      switch (funnel) {
        case Funnel.Lodging: {
          const lodgingRequest: LodgingAvailabilityRequest = {
            // Only include dynamic fields for flight+hotel packages
            // For hotel-only or hotel+car packages, use exact dates
            ...(includesFlights && {
              dynamicFields: {
                adjustStartDateUsingLanding: {
                  date: requestedSearch.dateRange.from,
                  originRegion: origin.airportCode.code,
                },
                adjustEndDateUsingTakeoff: {
                  date: requestedSearch.dateRange.until,
                  originRegion: destination.airportCode.code,
                },
              },
            }),
            LodgingAvailabilityRequest:
              PackageAvailabilityRequestEnum.InitialRequest,
            lodgingSelection: destination.lodgingsId.lodgingSelection,

            stayDates: {
              from: requestedSearch.dateRange.from,
              until: requestedSearch.dateRange.until,
            },
            guests: {
              adults: requestedSearch.guests.adultCount,
              // Infants are flight-only pax types; hotels treat them as young
              // children. Lap infants → age 0, seat infants → age 1.
              children: buildLodgingChildrenAges(
                requestedSearch.guests.childAges,
                requestedSearch.guests.infantOnLapAges.length,
                requestedSearch.guests.infantInSeatAges.length
              ),
            },
            rooms: { numberOfRooms: requestedSearch.roomCount },
            platform: isDesktopAndUp ? Platform.Desktop : Platform.Mobile,
          };
          products.push({
            lodgingAvailabilityRequest: lodgingRequest,
          });
          break;
        }
        case Funnel.Air: {
          if (chosenFlight) {
            products.push({ chosenProduct: chosenFlight });
          } else {
            const flightRequest: FlightShopRequest = {
              route: {
                origin: origin.airportCode.code,
                destination: destination.airportCode.code,
              },
              departureDate: requestedSearch.dateRange.from,
              returnDate: requestedSearch.dateRange.until,
              passengers: buildPackageFlightShopPassengers(
                requestedSearch.guests
              ),
              tripFilter: ShopFilter.NoFilter,
              fareClass: [],
            };
            products.push({ flightShopRequest: flightRequest });
          }
          break;
        }
        case Funnel.Ground: {
          // Cars are always inserted at index 1 (2nd position) in products array
          // This makes the backend happy when doing requisition
          const { pickupDate, dropoffDate } =
            useFormatPackagePickupDropoffDates(
              requestedSearch.dateRange.from,
              requestedSearch.dateRange.until
            );
          const { startDate, endDate } = formatPackageDynamicFieldDates(
            requestedSearch.dateRange.from,
            requestedSearch.dateRange.until
          );

          const carRequest: CarAvailabilityRequest = {
            // Only include dynamic fields for packages with flights
            // For hotel+car packages, use exact pickup/dropoff times
            ...(includesFlights && {
              dynamicFields: {
                adjustStartDateUsingLanding: {
                  date: startDate,
                  originRegion: origin.airportCode.code,
                },
                adjustEndDateUsingTakeoff: {
                  date: endDate,
                  originRegion: destination.airportCode.code,
                },
              },
            }),
            pickupSelection: {
              CodeContext: CodeContextEnum.GooglePlace,
              code: destination.groundSelection.groundSelection.placeId,
            },
            dropOffSelection: {
              CodeContext: CodeContextEnum.GooglePlace,
              code: destination.groundSelection.groundSelection.placeId,
            },
            driverAge: requestedSearch.driverAge,
            pickupDateTime: pickupDate,
            dropoffDateTime: dropoffDate,
          };
          // Insert car at index 1 (2nd position) instead of pushing to end
          products.splice(1, 0, {
            carAvailabilityRequest: carRequest,
          });
          break;
        }
        case Funnel.Package:
        case Funnel.Activities: {
          throw new Error(`${funnel} not accepted for requisition`);
        }
        default: {
          ensureExhaustive(funnel);
        }
        // Can add other funnel types here if more is added to packages.
      }
    }

    // Check abort again after building the products array — the loop itself
    // is synchronous but a concurrent search may have aborted while we were
    // awaiting fetchPackageSearchParams above.
    if (thunkAPI.signal.aborted) {
      return thunkAPI.rejectWithValue("Aborted by newer search");
    }

    // Add package type to base state
    thunkAPI.dispatch(
      PackagesBaseActions.setCurrentActivePackageType(
        requestedSearch.packageType
      )
    );

    // based off of what package type that's the fetch call we should do to trigger hotels
    switch (requestedSearch.packageType) {
      case PackageType.HOTEL_FLIGHT_CAR:
      case PackageType.HOTEL_CAR:
      case PackageType.HOTEL_FLIGHT: {
        // Start with Hotels
        thunkAPI.dispatch(
          fetchPackageHotelAvailability({
            search: resolvedSearch as PackagesSearch,
            requisition: { products },
          })
        );
        break;
      }
      case PackageType.FLIGHT_CAR: {
        thunkAPI.dispatch(
          fetchPackageFlightsRequisition({
            search: resolvedSearch as PackagesSearch,
            requisition: { products },
          })
        );
        break;
      }
      default: {
        ensureExhaustive(requestedSearch.packageType);
      }
    }

    return {
      searchFormValues: resolvedSearch,
      requestedSearch: resolvedSearch as PackagesSearch,
    };
  } catch (error) {
    console.log(error);
    // Log and reject with detailed error message
    logger.debug(`Error fetching search locations. ${error}`);
    const errorMessage = error instanceof Error ? error.message : String(error);
    return thunkAPI.rejectWithValue(errorMessage);
  }
});
